import React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import useEscapeKey from '../../hooks/useEscapeKey';

const SIZE_CLASSES = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-2xl', xl: 'max-w-4xl' };
const TONE_CLASSES = {
  light: { panel: 'bg-white', header: 'border-slate-200', title: 'text-slate-900', close: 'text-slate-400 hover:text-slate-600 hover:bg-slate-100' },
  dark: { panel: 'bg-slate-900 border border-slate-800', header: 'border-slate-800', title: 'text-white', close: 'text-slate-500 hover:text-slate-300 hover:bg-slate-800' },
};

// Single modal chrome — replaces 4 divergent ad hoc dialog shapes
// (different radii/shadows/max-widths) used across the app.
const Modal = ({ open, onClose, title, size = 'md', tone = 'light', footer, children }) => {
  const { t } = useTranslation();
  useEscapeKey(() => onClose?.(), open);
  if (!open) return null;

  const toneClasses = TONE_CLASSES[tone];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={['w-full rounded-2xl shadow-2xl flex flex-col max-h-[90vh]', SIZE_CLASSES[size], toneClasses.panel].join(' ')}
      >
        {title && (
          <div className={['flex items-center justify-between px-6 py-4 border-b flex-shrink-0', toneClasses.header].join(' ')}>
            <h3 className={['text-lg font-semibold', toneClasses.title].join(' ')}>{title}</h3>
            <button
              type="button"
              onClick={onClose}
              className={['p-1.5 rounded-lg transition-colors', toneClasses.close].join(' ')}
              aria-label={t('common.close')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}
        <div className="px-6 py-5 overflow-y-auto">{children}</div>
        {footer && (
          <div className={['px-6 py-4 border-t flex justify-end gap-3 flex-shrink-0', toneClasses.header].join(' ')}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
